import { lazy } from "react";

import PrivateRoute from "@/components/auth/PrivateRoute";
import PublicRoute from "@/components/auth/PublicRoute";
import PermissionGuard from "@/components/common/PermissionGuard";
import RootRedirect from "@/components/common/RootRedirect";
import DebugRoute from "@/components/debug/DebugRoute";
import MasterLayout from "@/layouts/MasterLayout";
import RootLayout from "@/layouts/RootLayout";
import CookiePreferences from "@/pages/legal/CookiePreferences";
import DataProtection from "@/pages/legal/DataProtection";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfUse from "@/pages/legal/TermsOfUse";
import Page404 from "@/pages/Status/Page404";
import { type AppRouteObject } from "@/router/interface";

// Lazy load the routes to improve performance
const Home = lazy(() => import("@/pages/Home"));
const Staff = lazy(() => import("@/pages/Staff"));
const Updates = lazy(() => import("@/pages/Updates"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Applications = lazy(() => import("@/pages/Applications"));
const Messages = lazy(() => import("@/pages/Messages"));
const Settings = lazy(() => import("@/pages/Settings"));
const Calendar = lazy(() => import("@/pages/Calendar"));
const Documents = lazy(() => import("@/pages/Documents"));
const Finance = lazy(() => import("@/pages/Finance"));
const CatchRides = lazy(() => import("@/pages/landing/CatchRides"));
const FindingScooter = lazy(() => import("@/pages/landing/FindingScooter"));
const RestaurantManagement = lazy(
  () => import("@/pages/landing/RestaurantManagement"),
);
const AppDownloads = lazy(
  () => import("@/pages/landing/AppDownloads"),
);
const HareengEatsLanding = lazy(
  () => import("@/pages/landing/HareengEatsLanding"),
);
const AboutUsLanding = lazy(() => import("@/pages/landing/AboutUsLanding"));
const CareersLanding = lazy(() => import("@/pages/landing/CareersLanding"));
const CommunityLanding = lazy(() => import("@/pages/landing/CommunityLanding"));
const SafetyLanding = lazy(() => import("@/pages/landing/SafetyLanding"));
const CitiesLanding = lazy(() => import("@/pages/landing/CitiesLanding"));
const DriverApplications = lazy(() => import("@/pages/DriverApplications"));
const DriverApplicationSubmission = lazy(
  () => import("@/pages/DriverApplicationSubmission"),
);
const DriverDashboard = lazy(() => import("@/pages/DriverDashboard"));
const DriverLandingPage = lazy(() => import("@/pages/DriverLandingPage"));

// Auth pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/auth/RegisterPage"));
const DriverRegisterPage = lazy(
  () => import("@/pages/auth/DriverRegisterPage"),
);
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));

// Demo page
const AuthDemo = lazy(() => import("@/pages/AuthDemo"));
const RouteTestPage = lazy(() => import("@/pages/RouteTestPage"));
const ContactUsLanding = lazy(() => import("@/pages/landing/ContactUs"));
const LandingPageHeroBackgroundsPage = lazy(
  () => import("@/pages/landing/LandingPageHeroBackgroundsPage"),
);
const SocialLinksPage = lazy(() => import("@/pages/admin/SocialLinksPage"));
const FAQPage = lazy(() => import("@/pages/admin/FAQPage"));
const ZonesAndSubzones = lazy(() => import("@/pages/admin/ZonesAndSubzones"));
const SubscriptionsManagement = lazy(() => import("@/pages/admin/SubscriptionsManagement"));
const NotificationsManagement = lazy(() => import("@/pages/admin/NotificationsManagement"));
const AuditManagement = lazy(() => import("@/pages/admin/AuditManagement"));
const DriverWalletManagement = lazy(() => import("@/pages/admin/DriverWalletManagement"));
const WalletSettingsPage = lazy(() => import("@/pages/admin/WalletSettingsPage"));

const routes: AppRouteObject[] = [
  {
    children: [
      // Root - redirects based on auth status and role
      {
        element: <RootRedirect />,
        index: true,
      },

      // PUBLIC AUTH ROUTES
      {
        element: (
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        ),
        path: "auth/login",
      },
      {
        element: (
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        ),
        path: "auth/register",
      },
      {
        element: (
          <PublicRoute>
            <DriverRegisterPage />
          </PublicRoute>
        ),
        path: "auth/driver/register",
      },
      {
        element: (
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        ),
        path: "auth/forgot-password",
      },
      {
        element: (
          <PublicRoute>
            <ResetPasswordPage />
          </PublicRoute>
        ),
        path: "auth/reset-password",
      },

      // ADMIN/STAFF/MANAGER DASHBOARD ROUTES
      {
        children: [
          {
            element: (
              <PermissionGuard requiredPermissions={["view_dashboard"]}>
                <Home />
              </PermissionGuard>
            ),
            index: true,
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["user_management"]}>
                <Staff />
              </PermissionGuard>
            ),
            path: "staff",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["updates_management"]}>
                <Updates />
              </PermissionGuard>
            ),
            path: "updates",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["analytics_view"]}>
                <Analytics />
              </PermissionGuard>
            ),
            path: "analytics",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["application_management"]}>
                <Applications />
              </PermissionGuard>
            ),
            path: "applications",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["financial_data"]}>
                <Finance />
              </PermissionGuard>
            ),
            path: "finance",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["customer_support"]}>
                <Messages />
              </PermissionGuard>
            ),
            path: "messages",
          },
          {
            element: <Settings />,
            path: "settings",
          },
          {
            element: <Calendar />,
            path: "calendar",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["Documents_management"]}>
                <Documents />
              </PermissionGuard>
            ),
            path: "documents",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <CatchRides />
              </PermissionGuard>
            ),
            path: "catch-rides",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <FindingScooter />
              </PermissionGuard>
            ),
            path: "finding-scooter",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <RestaurantManagement />
              </PermissionGuard>
            ),
            path: "restaurant-management",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <AppDownloads />
              </PermissionGuard>
            ),
            path: "app-downloads",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <HareengEatsLanding />
              </PermissionGuard>
            ),
            path: "hareeng-eats-landing",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <AboutUsLanding />
              </PermissionGuard>
            ),
            path: "about-us-landing",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <CareersLanding />
              </PermissionGuard>
            ),
            path: "careers-landing",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <CommunityLanding />
              </PermissionGuard>
            ),
            path: "community-landing",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <SafetyLanding />
              </PermissionGuard>
            ),
            path: "safety-landing",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["landing_pages_control"]}>
                <CitiesLanding />
              </PermissionGuard>
            ),
            path: "cities-landing",
          },
          {
            element: <DriverApplications />,
            path: "driver-applications",
          },
          {
            element: <DriverApplicationSubmission />,
            path: "apply-driver",
          },
          {
            element: <AuthDemo />,
            path: "auth-demo",
          },
          {
            element: <ContactUsLanding />,
            path: "contact-us-landing",
          },
          {
            element: <LandingPageHeroBackgroundsPage />,
            path: "landing-page-hero-backgrounds",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["social_links"]}>
                <SocialLinksPage />
              </PermissionGuard>
            ),
            path: "social-links",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["FAQs"]}>
                <FAQPage />
              </PermissionGuard>
            ),
            path: "faqs",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["zones_&_subzones"]}>
                <ZonesAndSubzones />
              </PermissionGuard>
            ),
            path: "zones-and-subzones",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["upcoming_subscriptions"]}>
                <SubscriptionsManagement />
              </PermissionGuard>
            ),
            path: "maintenance-subscriptions",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["view_notifications"]}>
                <NotificationsManagement />
              </PermissionGuard>
            ),
            path: "notifications",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["view_audit_trail"]}>
                <AuditManagement />
              </PermissionGuard>
            ),
            path: "audits",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["wallet_management"]}>
                <DriverWalletManagement />
              </PermissionGuard>
            ),
            path: "wallets",
          },
          {
            element: (
              <PermissionGuard requiredPermissions={["wallet_management"]}>
                <WalletSettingsPage />
              </PermissionGuard>
            ),
            path: "wallet-settings",
          },
        ],
        element: (
          <PrivateRoute requiredRoles={["admin", "manager", "staff"]}>
            <MasterLayout />
          </PrivateRoute>
        ),
        path: "dashboard",
      },

      // DRIVER DASHBOARD ROUTES
      {
        children: [
          {
            element: <DriverDashboard />,
            index: true,
          },
          {
            element: <DriverApplications />,
            path: "applications",
          },
          {
            element: <DriverApplicationSubmission />,
            path: "apply",
          },
          {
            element: <Settings />,
            path: "settings",
          },
          {
            element: <Messages />,
            path: "messages",
          },
        ],
        element: (
          <PrivateRoute requiredRoles={["driver"]}>
            <MasterLayout />
          </PrivateRoute>
        ),
        path: "dashboard/driver",
      },

      // PUBLIC PAGES
      {
        element: <RouteTestPage />,
        path: "test-routes",
      },
      {
        element: <DriverLandingPage />,
        path: "driver",
      },
      {
        element: <TermsOfUse />,
        path: "terms",
      },
      {
        element: <PrivacyPolicy />,
        path: "privacy",
      },
      {
        element: <CookiePreferences />,
        path: "pages/legal/cookie-preferences",
      },
      {
        element: <DataProtection />,
        path: "pages/legal/data-protection",
      },

      // 404 FALLBACK
      {
        element: <Page404 />,
        path: "*",
      },
    ],
    element: <RootLayout />,
    path: "/",
  },
];

export default routes;
